/**
 * Where the assistant sits when it has been dragged off its corner.
 *
 * The stored value is the dock's top-left in client pixels. Dragging works by
 * freezing wherever the placement classes had already put it and then applying
 * pointer deltas, so no part of this has to know how tall the bubble or the
 * popover above the avatar happen to be.
 *
 * A per-viewer convenience, like the Vault's view mode: it lives in
 * localStorage rather than on the profile, because "which corner" is the
 * setting that syncs across devices and this is just where you shoved it on
 * this screen. Clearing it falls back to that setting.
 */

export interface DockPoint {
  x: number;
  y: number;
}

export interface DockSize {
  width: number;
  height: number;
}

export const DOCK_POSITION_KEY = "weco.mochi.dockPosition";

/** Pointer travel, in px, past which a press is a drag and not a click.
 *
 * 4px was too eager: an ordinary click drifts a couple of pixels, and on a
 * trackpad rather more, so pressing Mochi to open it nudged it across the
 * screen instead. This wants to be a distance nobody reaches by accident
 * while clicking, but still a short, deliberate drag. */
export const DRAG_THRESHOLD = 10;

/** Breathing room kept between the dock and the edge of the window. */
const MARGIN = 8;

/** A press that has not moved this far is still a click, and must open the
 *  popover rather than being swallowed as a tiny drag. */
export function movedEnoughToDrag(dx: number, dy: number, threshold = DRAG_THRESHOLD): boolean {
  return Math.hypot(dx, dy) >= threshold;
}

/**
 * Keep the dock on screen.
 *
 * Applied while dragging AND on resize: a position saved on a wide monitor
 * would otherwise leave the assistant somewhere off the side of a laptop
 * screen, with no way to get it back short of clearing storage.
 */
export function clampDock(at: DockPoint, size: DockSize, viewport: DockSize): DockPoint {
  // A dock bigger than the window (a very short window, a very tall popover)
  // has no valid range; pin it to the top-left rather than returning a max
  // below the min, which would place it off the opposite edge.
  const maxX = Math.max(MARGIN, viewport.width - size.width - MARGIN);
  const maxY = Math.max(MARGIN, viewport.height - size.height - MARGIN);
  return {
    x: Math.min(Math.max(at.x, MARGIN), maxX),
    y: Math.min(Math.max(at.y, MARGIN), maxY),
  };
}

/**
 * The saved position, but only if it belongs to the corner currently chosen in
 * Settings.
 *
 * Storing the placement alongside it is what makes that setting authoritative
 * again: pick a different corner and the drag is forgotten, on this render and
 * after a reload. Without the stamp, a dragged position outranked the setting
 * forever and choosing a corner appeared to do nothing.
 */
export function readDockPosition(placement?: string): DockPoint | null {
  try {
    const raw = localStorage.getItem(DOCK_POSITION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<DockPoint> & { placement?: string };
    // Anything non-finite would become a NaN in a style property, which the
    // browser drops — leaving the dock wherever it last painted with no way
    // to tell that the stored value is the reason.
    if (!Number.isFinite(parsed?.x) || !Number.isFinite(parsed?.y)) return null;
    // An undefined `placement` argument means "whatever was saved" — used by
    // callers that only want to know whether anything is stored at all.
    if (placement !== undefined && parsed.placement !== undefined && parsed.placement !== placement) {
      return null;
    }
    return { x: parsed.x as number, y: parsed.y as number };
  } catch {
    return null;
  }
}

export function writeDockPosition(at: DockPoint, placement?: string): void {
  try {
    localStorage.setItem(
      DOCK_POSITION_KEY,
      JSON.stringify({ x: at.x, y: at.y, placement }),
    );
  } catch {
    /* private mode, blocked storage — dragging still works for this session */
  }
}

export function clearDockPosition(): void {
  try {
    localStorage.removeItem(DOCK_POSITION_KEY);
  } catch {
    /* ignore */
  }
}
